// Sentry/GlitchTip initialisation, gated on the `statistics` cookie consent category.
//
// We deliberately do NOT wrap the Sentry API. Call sites import `@sentry/vue` directly, and
// network errors go through `captureApiError` (src/sentry/network.ts). This module owns three
// things: building the init config, running init exactly once, and the consent gate that decides
// WHEN init may run. Ported from med-web-funnel `src/modules/sentry/init.ts`.
//
// Error/perf monitoring is classified under `statistics` (analytics), so we gate on exactly that
// category. Hard requirement satisfied: no Sentry network traffic until statistics consent is
// active — Sentry.init() (hence the transport) does not run before then. Errors before that are
// not captured.
import type { App } from 'vue'
import * as Sentry from '@sentry/vue'

import { config } from '@/config'
import { useCookieConsentStore } from '@/store/cookieConsent'
import { useGeoStore } from '@/store/geo'
import { useLocationStore } from '@/store/location'

// Sentry is error/perf monitoring → the `statistics` (analytics) consent category.
const SENTRY_CONSENT_CATEGORY = 'statistics'

let initialised = false

function buildInitialScope() {
	// Read store state at init time — by the time statistics consent is granted these are
	// populated: geo from the Cloudflare-injected `window.__GEO__`, sessionId from
	// `useLocationStore().init()` (run at bootstrap). Both are static for the session, so unlike
	// med-web-funnel there is no App.vue watcher needed to keep these tags current.
	const geo = useGeoStore()
	return {
		tags: {
			countryCode: geo.country ?? undefined,
			regionCode: geo.regionCode ?? undefined,
			hostname: window.location.hostname,
		},
		// Opaque per-session id (the analytics sessionId) — never the Auth0 `sub` or email (PII).
		user: { id: useLocationStore().sessionId },
	}
}

// Object keys whose values are PII/PHI and must never reach Sentry. Matched case-insensitively
// against keys when redacting console breadcrumbs.
const PII_KEY_RE =
	/(^|_)(e?mail|name|firstname|lastname|fullname|phone|tel|dob|dateofbirth|birth|address|street|city|zip|postal|postalcode|state|password|passwd|secret|token|authorization|auth|cookie|session|signature|ssn|patient)(_|$)/i

// Deep-clone `value`, replacing PII-keyed fields with '[redacted]' and collapsing Error objects
// to { name, message } so an AxiosError-style config/response/headers never rides along. Depth-
// and cycle-bounded.
function redactPii(value: unknown, seen: WeakSet<object> = new WeakSet(), depth = 0): unknown {
	if (value === null || typeof value !== 'object' || depth > 6) return value
	if (seen.has(value)) return '[circular]'
	seen.add(value)
	if (value instanceof Error) return { name: value.name, message: value.message }
	if (Array.isArray(value)) return value.map((item) => redactPii(item, seen, depth + 1))
	const out: Record<string, unknown> = {}
	for (const [key, val] of Object.entries(value)) {
		out[key] = PII_KEY_RE.test(key) ? '[redacted]' : redactPii(val, seen, depth + 1)
	}
	return out
}

// Render one console argument as a readable, redacted string (no "[object Object]").
function readableArg(value: unknown): string {
	if (typeof value === 'string') return value
	try {
		return JSON.stringify(redactPii(value))
	} catch {
		return String(value)
	}
}

// Reformat console breadcrumbs into a readable, PII-redacted form; pass everything else through
// untouched. Never drops a breadcrumb.
function redactConsoleBreadcrumb(breadcrumb: Sentry.Breadcrumb, hint?: Sentry.BreadcrumbHint): Sentry.Breadcrumb {
	if (breadcrumb.category !== 'console') return breadcrumb
	const input = hint?.input
	if (Array.isArray(input)) {
		const parts = input.map(readableArg)
		breadcrumb.message = parts.join(' ')
		if (breadcrumb.data) breadcrumb.data.arguments = parts
	}
	return breadcrumb
}

/** Initialise Sentry exactly once. No-op if already initialised or the DSN is unset. */
function initSentry(app: App): void {
	if (initialised) return
	const dsn = config.sentryDsn
	if (!dsn) return
	initialised = true

	Sentry.init({
		app,
		dsn,
		// Local dev (`npm run serve` sets VITE_APP_VERSION=local) reports as its own
		// `local` environment in GlitchTip instead of being lumped in with CI `development`.
		environment: config.appVersion === 'local' ? 'local' : import.meta.env.MODE,
		release: config.appVersion,
		// SDK-level debug logging in dev/staging (the dev build), quiet in prod.
		debug: import.meta.env.DEV,
		sendDefaultPii: false,
		// @sentry/vue attaches component props to contexts.vue.propsData on every component error
		// (attachProps defaults to true); props can carry PII (a component may receive user/email/
		// etc. as props). Disable that.
		attachProps: false,
		// extraErrorDataIntegration intentionally NOT added: it serialises every non-native
		// enumerable prop of an error (and error.cause) into the event — for an API error that can
		// mean request body, headers and response.data (PII/PHI). Without it a raw error contributes
		// only message + stack, which are safe.
		integrations: [Sentry.captureConsoleIntegration({ levels: ['error'] })],
		// Extension point: this project grows its own ignore list as the dashboard reveals real
		// noise. Intentionally NOT seeded from other projects.
		ignoreErrors: [],
		// Extension point for project-specific filtering. Identity for now.
		beforeSend: (event) => event,
		// Console breadcrumbs otherwise render as "[object Object]" (message) and depth-truncated
		// "[Object]" (data.arguments). Reformat into a readable, PII-redacted string instead.
		beforeBreadcrumb: redactConsoleBreadcrumb,
		initialScope: buildInitialScope(),
	})
}

/**
 * Initialise Sentry. Outside production (PR/dev/local) there is often no Cookiebot banner — it is
 * skipped on *.workers.dev preview hosts — so `statistics` consent never flips and Sentry would
 * never start, hiding every error from those environments; there we init immediately. In
 * production we keep the strict gate: init only once statistics consent is active (subscribed
 * before the banner mounts to catch the first accept; also handles the already-consented
 * returning-user / opt-out case). PHI/PII scrubbing in initSentry applies regardless of consent.
 */
export function setupSentryConsentGate(app: App): void {
	// Consent is enforced only in production; PR/dev/local initialise without waiting for it.
	if (import.meta.env.DEV) {
		initSentry(app)
		return
	}

	const consent = useCookieConsentStore()

	const tryInit = () => {
		if (consent.categories[SENTRY_CONSENT_CATEGORY] === true) initSentry(app)
	}

	tryInit()
	if (initialised) return

	const unsubscribe = consent.$subscribe(() => {
		tryInit()
		if (initialised) unsubscribe()
	})
}
